import { Checkbox, FormLabel, Grid, makeStyles, MenuItem, Select, Typography } from "@material-ui/core";
import { AssignmentInd, Block } from "@material-ui/icons";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import React from "react";
import { useIntl } from "react-intl";
import { LanguageTranslationKeys, SwissGermanLocale, SwissTimeZone } from "../../../data/constants";
import {
  CandidateModuleState,
  CandidateModuleStateTranslationKey,
} from "../../../model/examSessionCandidate/CandidateModuleState";
import { ExamSessionCandidateModuleViewModel } from "../../../model/examSessionCandidate/ExamSessionCandidateModuleViewModel";
import { ExamSessionCandidateViewModel } from "../../../model/examSessionCandidate/ExamSessionCandidateViewModel";
import { OralExamOption, OralExamOptionTranslationKey } from "../../../model/OralExamOption";
import { GetTranslatedValueIntl } from "../../../model/Shared/TranslatedValue";
import TrainingProvider from "../../../model/TrainingProvider";
import { TranslationKey } from "../../../translations/TranslationKey";
import CustomButton from "../../common/buttons/CustomButton";
import FormGridCell from "../../common/FormGridCell";
import TitleWithIcon from "../../common/TitleWithIcon";
import TranslatedMessage from "../../translations/TranslatedMessage";
import PaymentStatus from "../../common/payment/PaymentStatus";
import { PaymentState } from "../../payment/PaymentConstants";

interface ExamSessionCandidateInfoProps {
  examSessionCandidate: ExamSessionCandidateViewModel;
  module: ExamSessionCandidateModuleViewModel;
  trainingProviders: TrainingProvider[];
  refundStatusChange: (value: boolean) => void;
  optionChange: (value: OralExamOption) => void;
  trainingProviderChange: (value: string) => void;
  isRepeatingChange: (value: boolean) => void;
  publishResultsToEmployerChange: (value: boolean) => void;
  validateModule: (approved: boolean) => void;
}

interface FormRowProps {
  titleId: any;
}

const useStyles = makeStyles((theme) => ({
  labelContainer: {
    display: "flex",
    alignItems: "center",
  },
  checkbox: {
    marginLeft: -12,
  },
  verificationButtons: {
    marginLeft: -7,
  },
  rejected: {
    marginLeft: theme.spacing(1),
  },
  separator: {
    height: theme.spacing(3),
  },
}));

const isRepeatingFalseValue = 0;
const isRepeatingTrueValue = 1;

const ExamSessionCandidateInfo: React.FunctionComponent<ExamSessionCandidateInfoProps> = ({
  examSessionCandidate,
  module,
  trainingProviders,
  refundStatusChange,
  optionChange,
  trainingProviderChange,
  isRepeatingChange,
  publishResultsToEmployerChange,
  validateModule,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const moduleName = GetTranslatedValueIntl(intl, module.name);

  const handleRefundStatusChange = (event: React.ChangeEvent<{ value: unknown }>) =>
    refundStatusChange(event.target.value as boolean);

  const handleOptionChange = (event: React.ChangeEvent<{ value: unknown }>) =>
    optionChange(event.target.value as OralExamOption);

  const handleTrainingProviderChange = (event: React.ChangeEvent<{ value: unknown }>) =>
    trainingProviderChange(event.target.value as string);

  const handleIsRepeatingChange = (event: React.ChangeEvent<{ value: unknown }>) =>
    isRepeatingChange((event.target.value as number) === isRepeatingTrueValue);

  const handlePublishResultsToEmployerChange = (event: React.ChangeEvent<{ value: unknown }>, checked: boolean) =>
    publishResultsToEmployerChange(checked);

  const handleApproveModule = () => validateModule(true);

  const handleRejectModule = () => validateModule(false);

  const getAverageGrade = () => {
    const { averageGrade } = module;
    return averageGrade === null || averageGrade === undefined ? "-" : averageGrade;
  };

  const getIsRepeatingValue = () => {
    if (module.isRepeating === undefined) {
      return undefined;
    }

    return module.isRepeating ? isRepeatingTrueValue : isRepeatingFalseValue;
  };

  const FormGridRow: React.FC<FormRowProps> = ({ titleId, children }) => {
    return (
      <>
        <FormGridCell size="half" className={classes.labelContainer}>
          <FormLabel component="legend">
            <Typography variant="body1" component="span">
              <TranslatedMessage id={titleId} />
            </Typography>
          </FormLabel>
        </FormGridCell>
        <FormGridCell size="half">
          <Typography variant="body1" component="span">
            {children}
          </Typography>
        </FormGridCell>
        <FormGridCell size="fill" />
      </>
    );
  };

  return (
    <>
      <TitleWithIcon title={<TranslatedMessage id="examSessionCandidate.orderDetails" />} variant="h5">
        <AssignmentInd fontSize="small" />
      </TitleWithIcon>
      <Grid container={true} spacing={1}>
        <FormGridRow titleId="common.orderId">{module.orderId}</FormGridRow>
        <FormGridRow titleId="common.transaction.id">{module.transactionId}</FormGridRow>
        <FormGridRow titleId="examSessionCandidate.paymentState">
          {module.paymentState === PaymentState.Abacus ? (
            <Select value={module.refundStatus} onChange={handleRefundStatusChange}>
              <MenuItem key={String(false)} value={String(false)}>
                <TranslatedMessage id="paymentStatus.paid.abacus" />
              </MenuItem>
              <MenuItem key={String(true)} value={String(true)}>
                <TranslatedMessage id="paymentStatus.refunded.abacus" />
              </MenuItem>
            </Select>
          ) : (
            <PaymentStatus state={module.paymentState} method={module.paymentMethod} refunded={module.refundStatus} />
          )}
        </FormGridRow>
        <FormGridRow titleId="examSessionCandidate.bookingTimestamp">
          {module.bookingTimestamp
            ? new Date(module.bookingTimestamp).toLocaleString(SwissGermanLocale, { timeZone: SwissTimeZone })
            : ""}
        </FormGridRow>
        <FormGridRow titleId="examSessionCandidate.bookedBy">{module.bookedBy}</FormGridRow>
        <FormGridRow titleId="examSessionCandidate.cancellationTimestamp">
          {module.cancellationTimestamp
            ? new Date(module.cancellationTimestamp).toLocaleString(SwissGermanLocale, { timeZone: SwissTimeZone })
            : ""}
        </FormGridRow>
        <FormGridRow titleId="examSessionCandidate.cancelledBy">{module.cancelledBy}</FormGridRow>
      </Grid>
      <div className={classes.separator} />
      <TitleWithIcon title={<TranslatedMessage id="examSessionCandidate.certification" />} variant="h5">
        <AssignmentInd fontSize="small" />
      </TitleWithIcon>
      <Grid container={true} spacing={1}>
        <FormGridRow titleId="person.field.displayName">
          {`${examSessionCandidate.firstName} ${examSessionCandidate.lastName}`}
        </FormGridRow>
        <FormGridRow titleId="common.debtorNumber">{module.abacusDebtorNumber}</FormGridRow>
        <FormGridRow titleId="person.field.email">{examSessionCandidate.email}</FormGridRow>
        <FormGridRow titleId="common.language">
          <TranslatedMessage id={LanguageTranslationKeys[examSessionCandidate.language]} />
        </FormGridRow>
        <FormGridRow titleId="person.field.employer">{examSessionCandidate.employer}</FormGridRow>
        <FormGridRow titleId="examSessionCandidate.state">
          {CandidateModuleStateTranslationKey[module.state] && (
            <>
              <TranslatedMessage id={CandidateModuleStateTranslationKey[module.state] as TranslationKey} />
              {module.isRejected && (
                <span className={classes.rejected}>
                  (<TranslatedMessage id="common.rejected" />)
                </span>
              )}
            </>
          )}
        </FormGridRow>
        <FormGridRow titleId="examSessionCandidate.averageGrade">{getAverageGrade()}</FormGridRow>
        <FormGridRow titleId="examSessionCandidate.module">{moduleName}</FormGridRow>
        <FormGridRow titleId="examSessionCandidate.option">
          <Select value={module.option} onChange={handleOptionChange}>
            <MenuItem key={OralExamOption.KMU} value={OralExamOption.KMU}>
              {<TranslatedMessage id={OralExamOptionTranslationKey[OralExamOption.KMU]} />}
            </MenuItem>
            <MenuItem key={OralExamOption.Privathaushalte} value={OralExamOption.Privathaushalte}>
              {<TranslatedMessage id={OralExamOptionTranslationKey[OralExamOption.Privathaushalte]} />}
            </MenuItem>
          </Select>
        </FormGridRow>

        <FormGridRow titleId="startCertification.modulesDetailsStep.trainingProvider">
          <Select value={module.trainingProvider} onChange={handleTrainingProviderChange}>
            {trainingProviders.map((trainingProvider) => (
              <MenuItem key={trainingProvider.key} value={trainingProvider.key}>
                {GetTranslatedValueIntl(intl, trainingProvider.label)}
              </MenuItem>
            ))}
          </Select>
        </FormGridRow>

        <FormGridRow titleId="examSessionCandidate.isRepeater">
          <Select value={getIsRepeatingValue()} onChange={handleIsRepeatingChange}>
            <MenuItem value={isRepeatingTrueValue}>
              <TranslatedMessage id="common.yes" />
            </MenuItem>
            <MenuItem value={isRepeatingFalseValue}>
              <TranslatedMessage id="common.no" />
            </MenuItem>
          </Select>
        </FormGridRow>

        <FormGridRow titleId="examSessionCandidate.publishResultsToEmployer">
          <Checkbox
            className={classes.checkbox}
            checked={examSessionCandidate.publishResultsToEmployer}
            onChange={handlePublishResultsToEmployerChange}
          />
        </FormGridRow>

        {module.state === CandidateModuleState.VerificationRequired && (
          <FormGridRow titleId="examSessionCandidate.verificationRequired">
            <Grid container={true} className={classes.verificationButtons}>
              <CustomButton tooltip={<TranslatedMessage id="common.accept" />} onClick={handleApproveModule}>
                <CheckCircleOutlineIcon />
              </CustomButton>
              <CustomButton tooltip={<TranslatedMessage id="common.reject" />} onClick={handleRejectModule}>
                <Block />
              </CustomButton>
            </Grid>
          </FormGridRow>
        )}
      </Grid>
    </>
  );
};

export default ExamSessionCandidateInfo;
